import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Copy, CheckCircle, RefreshCw } from 'lucide-react';
import PasswordRequirements, { usePasswordRequirements } from './PasswordRequirements';
import '../styles/Modal.css';

function ResetPasswordModal({ isOpen, onClose, onConfirm, user, newPassword }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [password, setPassword] = useState(newPassword);
  
  // Use the shared password requirements hook
  const { requirements, isPasswordValid } = usePasswordRequirements();

  useEffect(() => {
    if (isOpen) {
      setPassword(newPassword);
      setShowPassword(false);
      setCopied(false);
    }
  }, [isOpen, newPassword]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const generateNewPassword = () => {
    const length = 12;
    let charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    // Add special characters if required
    if (requirements?.passwordRequireSpecialChars) {
      charset += '!@#$%^&*';
    }
    
    let newPwd = '';
    
    // Ensure at least one of each required character type
    if (requirements) {
      if (requirements.passwordRequireUppercase) {
        newPwd += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(Math.floor(Math.random() * 26));
      }
      if (requirements.passwordRequireLowercase) {
        newPwd += 'abcdefghijklmnopqrstuvwxyz'.charAt(Math.floor(Math.random() * 26));
      }
      if (requirements.passwordRequireNumbers) {
        newPwd += '0123456789'.charAt(Math.floor(Math.random() * 10));
      }
      if (requirements.passwordRequireSpecialChars) {
        newPwd += '!@#$%^&*'.charAt(Math.floor(Math.random() * 8));
      }
    }
    
    // Fill the rest randomly
    while (newPwd.length < length) {
      newPwd += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    // Shuffle the password
    newPwd = newPwd.split('').sort(() => Math.random() - 0.5).join('');
    
    setPassword(newPwd);
    setCopied(false);
  };

  if (!isOpen || !user) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = () => {
    if (!isPasswordValid(password)) {
      return;
    }
    onConfirm(password);
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-title-wrapper">
            <h3>Reset Password</h3>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          <p style={{ marginBottom: 'var(--spacing-lg)' }}>
            Reset password for <strong>{user.first_name} {user.last_name}</strong>?
          </p>
          
          <div className="password-display-container">
            <label className="password-display-label">New Temporary Password:</label>
            <div className="password-display">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                className="password-display-input"
                placeholder="Enter password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              <button
                type="button"
                className="password-copy-btn"
                onClick={handleCopy}
                title="Copy password"
              >
                {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
              </button>
              <button
                type="button"
                className="password-generate-btn"
                onClick={generateNewPassword}
                title="Generate new password"
              >
                <RefreshCw size={18} />
              </button>
            </div>
            {copied && (
              <small className="copy-success">Password copied to clipboard!</small>
            )}
          </div>

          <PasswordRequirements password={password} showAlways={true} />

          <div className="password-warning">
            <p><strong>⚠️ Important:</strong></p>
            <ul>
              <li>Make sure to copy this password and send it to the user securely</li>
              <li>The user should change this password after logging in</li>
              <li>This password will not be shown again</li>
            </ul>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn-warning" 
            onClick={handleConfirm}
            disabled={!isPasswordValid(password)}
          >
            Reset Password
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordModal;
